/**
 * The Quality Lessons Learned register (Ruling 18): the findings the lessons seam
 * marked, read through the finding register's own lesson_status filter.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * This register is a filter over the findings the server already answers with, not a
 * store of its own: the full lessons-learned module is a named later station. The
 * marking itself lives on a CLOSED finding's own page, operator side only; the server
 * refuses everyone else and that screen renders the refusal as it arrives.
 */

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { listFindings, type FindingSummary } from "../api/findings";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { AbsencePanel, EmptyState, SkeletonRows, day } from "./parts";
import { useQuery } from "./useQuery";

const PAGE_SIZE = 25;

const LESSON_STATUSES = ["NONE", "CANDIDATE", "CAPTURED"] as const;

/** The register's displayed label for a lesson status token. */
export function lessonLabel(status: string): string {
  if (status === "CANDIDATE") {
    return strings.lessons.statusCANDIDATE;
  }
  if (status === "CAPTURED") {
    return strings.lessons.statusCAPTURED;
  }
  return strings.lessons.statusNone;
}

function isLessonStatus(value: string | null): (typeof LESSON_STATUSES)[number] | null {
  return value !== null && (LESSON_STATUSES as readonly string[]).includes(value)
    ? (value as (typeof LESSON_STATUSES)[number])
    : null;
}

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The lessons register columns: what each cell says in plain words. */
function lessonRegisterColumns(): RegisterColumn<FindingSummary>[] {
  const copy = strings.lessons;
  return [
    {
      id: "finding",
      label: copy.columnFinding,
      text: (row) => `${row.humanId} ${row.description}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/findings/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.description}</div>
        </>
      ),
    },
    {
      id: "findingClass",
      label: copy.columnClass,
      text: (row) => termForClass(row.findingClass),
      filter: true,
    },
    {
      id: "status",
      label: copy.columnStatus,
      text: (row) => lessonLabel(row.lessonLearnedStatus),
      filter: true,
      render: (row) => (
        <span data-testid="lesson-status">{lessonLabel(row.lessonLearnedStatus)}</span>
      ),
    },
    {
      id: "observed",
      label: copy.columnObserved,
      text: (row) => day(row.observedAt),
      sortValue: (row) => row.observedAt,
      className: "whitespace-nowrap",
    },
  ];
}

export function LessonsPage(): React.JSX.Element {
  const token = useToken();
  const [parameters, setParameters] = useSearchParams();
  const offset = Number(parameters.get("offset") ?? "0");
  const status = isLessonStatus(parameters.get("status"));

  const register = useQuery(
    () =>
      listFindings(token, {
        lessonStatus: status,
        limit: PAGE_SIZE,
        offset,
      }),
    [token, status, offset],
  );

  const setParameter = (name: string, value: string): void => {
    const next = new URLSearchParams(parameters);
    if (value.length === 0) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
    next.delete("offset");
    setParameters(next);
  };

  const columns = useMemo(() => lessonRegisterColumns(), []);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.lessons.heading}</h2>
          <p className="q-shell__lede">{strings.lessons.lede}</p>
        </div>
      </header>

      <div className="q-ui mb-(--q-s1) flex flex-wrap items-center gap-3">
        <Label htmlFor="filter-lesson" className="text-sm text-muted-foreground">
          {strings.lessons.filterStatus}
        </Label>
        <div className="w-full sm:w-64">
          <NativeSelect
            id="filter-lesson"
            value={status ?? ""}
            data-testid="lesson-filter"
            onChange={(event) => {
              setParameter("status", event.target.value);
            }}
          >
            <option value="">{strings.lessons.all}</option>
            {LESSON_STATUSES.map((one) => (
              <option key={one} value={one}>
                {lessonLabel(one)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      <p className="q-note">{strings.lessons.registerHint}</p>

      <RegisterCard>
        {register.kind === "loading" && <SkeletonRows />}
        {register.kind === "absence" && <AbsencePanel />}
        {register.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (register.data.items.length === 0 ? (
            <EmptyState title={strings.lessons.emptyTitle} body={strings.lessons.emptyBody} />
          ) : (
            <DataTable
              data={register.data.items}
              columns={columns}
              caption={strings.lessons.heading}
              testId="lessons"
              getRowId={(row) => row.id}
              getRowTestId={() => "lesson-row"}
            />
          ))}
      </RegisterCard>
    </>
  );
}

function termForClass(token: string): string {
  const table: Record<string, string> = { ...strings.terms.findingClasses };
  return table[token] ?? strings.terms.fallback(token);
}
